/**
 * Where an aircraft's propeller disc sits and how big it is, measured off the
 * baked `.TBL` geometry — AD-012's "hub position and radius are measured from
 * the baked model", implemented as the same offline-derive/checked-in-table
 * shape `cockpit-eye.ts` uses for the pilot's eye
 * (`apps/web/src/flight/propeller-disc.ts` holds the table,
 * `apps/web/test/propeller-disc.test.ts` recomputes it against the real bake).
 *
 * ## The frame
 *
 * Identical to `cockpit-eye.ts`'s, and for the same reason: baked model space
 * is Z-up with the nose at +Y, `aircraft-mesh.ts` recentres a loaded mesh onto
 * its own bounding-box centre, and the render-time −90° X rotation carries
 * `(x, y, z)_model → (x, z, −y)_body`. So a model-space offset's components
 * land unchanged on the engine's body axes: model `y` is `forwardM`, model `z`
 * is `upM`, model `x` is `rightM` (always ~0 here — every airframe in the
 * fleet is symmetric about its centreline).
 *
 * ## What is measured, and what is not
 *
 * MEASURED, per aircraft, and genuinely per-model:
 *
 * - **The hub.** The forward-most vertex ring of the *fuselage* — vertices
 *   inside a centreline strip `FUSELAGE_STRIP_FRACTION` of the span wide, so
 *   a wing leading edge that reaches nearly as far forward as the nose
 *   (`CAMEL`'s does) cannot be mistaken for it. `forwardM` is that ring's `y`
 *   relative to the bounding-box centre; `upM` is the ring's own mean `z`
 *   relative to the same centre, which is what puts the disc on the thrust
 *   line rather than on the fuselage datum.
 *
 * INFERRED, from a measured proportion:
 *
 * - **The radius.** The original stores no propeller at all — no blade
 *   geometry, no diameter, nothing (AD-012's opening line), so there is no
 *   constant to recover and the disc's size can only be a port-side choice.
 *   It is taken as `PROP_RADIUS_PER_NOSE_RADIUS` times the airframe's own
 *   forward-fuselage cross-section (so a Handley Page's disc is not a Camel's)
 *   and then **clamped to the historical band** every single-engine type in
 *   this fleet actually lived in: 2.1–3.1 m diameter (Sopwith Camel 2.59 m,
 *   Fokker Dr.I 2.62 m, S.E.5a 2.51 m, SPAD XIII 2.50 m, Albatros D.Va
 *   2.63 m, Fokker D.VII 2.68 m). The clamp is the part that matters — it is
 *   what guarantees the proportion can never produce an absurd disc on a
 *   coarse 150-triangle model — and it is why `PROP_RADIUS_FLEET_M` below is
 *   a fleet *median* rather than an average of anything measured.
 *
 * ## Airframes this declines to classify
 *
 * `propellerHubFromLod` returns `undefined` when the nose ring is degenerate
 * (fewer than `MIN_NOSE_RING_VERTICES` vertices, or a zero cross-section) —
 * the caller falls back to `PROP_RADIUS_FLEET_M` at the raw nose tip. Pushers
 * and multi-engine types are a *different* question and are not this
 * function's business: their propellers are not on the nose at all, so the
 * runtime skips them by name rather than measuring a hub that would be in the
 * wrong place (see `apps/web/src/flight/propeller-disc.ts`'s `NO_NOSE_PROP`).
 */

/** Half-width of the fuselage sampling strip, as a fraction of the model's span. */
export const FUSELAGE_STRIP_FRACTION = 0.1;

/** How far back from the nose ring the cowl cross-section is sampled, as a fraction of the model's length. */
export const NOSE_DEPTH_FRACTION = 0.1;

/**
 * How far off the thrust line a vertex may sit and still count as cowl, as a
 * fraction of the model's total height. Without it the sample swallows the
 * undercarriage below and the upper wing above — both of which pass the
 * centreline-strip test on a biplane whose wheels are tucked under the engine.
 */
export const THRUST_LINE_GUARD_FRACTION = 0.25;

/**
 * How deep the nose ring is, as a fraction of the model's length. Not zero,
 * because several airframes are modelled with a *pointed* spinner whose tip is
 * a single vertex (`MORANE`, `AIRCO`, `HALBD2`) — a ring of exactly the
 * forward-most `y` would be one point on those and the measurement would
 * decline every one of them. 2 % of the length is one polygon band on these
 * 110-220 triangle models: enough to reach the ring behind a spinner tip,
 * nowhere near enough to reach the cockpit.
 */
export const NOSE_RING_DEPTH_FRACTION = 0.02;

/** Below this, the forward-most ring is a modelling artefact rather than a nose. */
const MIN_NOSE_RING_VERTICES = 3;

/**
 * Propeller radius per unit of forward-fuselage cross-section radius.
 *
 * CALIBRATED against the nine airframes in this fleet whose real propeller
 * diameter is documented — Nieuport 17 2.40 m, Sopwith Camel 2.59 m, Fokker
 * Dr.I 2.62 m, S.E.5a 2.51 m, SPAD XIII 2.50 m, Albatros D.Va 2.63 m, Fokker
 * D.VII 2.68 m, Sopwith Snipe 2.79 m, B.E.2c 2.90 m. Each gives its own ideal
 * factor (its real radius ÷ its measured nose radius); those nine run 1.91 to
 * 2.88 and their **median is 2.33**, which is what this rounds. The spread is
 * the models', not the aeroplanes': a 110-220 triangle airframe draws its
 * forward fuselage to whatever polygon budget was left, and no single factor
 * can fit them all. Residual error against the nine is ±16 %.
 */
export const PROP_RADIUS_PER_NOSE_RADIUS = 2.35;

/**
 * The historical band, as radii: 2.30 m to 3.00 m diameter, which brackets
 * every single-engine type in this fleet (the Nieuport 17's 2.40 m at the
 * bottom, the B.E.2c's 2.90 m at the top). The clamp, not the factor above, is
 * what guarantees a coarse model can never produce an absurd disc.
 */
export const PROP_RADIUS_MIN_M = 1.15;
export const PROP_RADIUS_MAX_M = 1.5;

/**
 * The documented fleet fallback for an airframe the measurement declines to
 * classify — the median of the fleet's real propeller radii (Camel 1.295 m,
 * Dr.I 1.31 m, Albatros D.Va 1.315 m, D.VII 1.34 m, S.E.5a 1.255 m, SPAD XIII
 * 1.25 m). AD-012 wrote this as "~1.4 m" before any of it was measured; 1.3 m
 * is the same number with the fleet's own figures behind it.
 */
export const PROP_RADIUS_FLEET_M = 1.3;

export interface PropellerHub {
  /** Metres forward of the recentred model origin, along `BODY_FORWARD`. */
  readonly forwardM: number;
  /** Metres above it, along `BODY_UP` — the thrust line, not the fuselage datum. */
  readonly upM: number;
  /** Disc radius in metres, already clamped to the historical band. */
  readonly radiusM: number;
}

/** The one LOD shape this reads — the same `positions` array `aircraft-mesh.ts` builds its geometry and bounding box from. */
export interface PropellerSourceLod {
  /** Flat x,y,z triplets in metres, model space (Z up, +Y nose). */
  readonly positions: readonly number[];
}

interface Extents {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
}

function extentsOf(positions: readonly number[]): Extents {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < positions.length; i += 3) {
    for (let axis = 0; axis < 3; axis++) {
      const value = positions[i + axis]!;
      if (value < min[axis]!) min[axis] = value;
      if (value > max[axis]!) max[axis] = value;
    }
  }
  return { min, max };
}

/** The forward-most fuselage `y` and the mean `z` of the ring sitting at it, or `undefined` for a degenerate nose. */
function noseRingOf(
  positions: readonly number[],
  stripHalfM: number,
  ringDepthM: number,
): { readonly noseY: number; readonly hubZ: number } | undefined {
  let noseY = -Infinity;
  for (let i = 0; i + 2 < positions.length; i += 3) {
    if (Math.abs(positions[i]!) > stripHalfM) continue;
    if (positions[i + 1]! > noseY) noseY = positions[i + 1]!;
  }
  if (!Number.isFinite(noseY)) return undefined;

  let count = 0;
  let sumZ = 0;
  for (let i = 0; i + 2 < positions.length; i += 3) {
    if (Math.abs(positions[i]!) > stripHalfM) continue;
    if (positions[i + 1]! < noseY - ringDepthM) continue;
    count++;
    sumZ += positions[i + 2]!;
  }
  return count >= MIN_NOSE_RING_VERTICES ? { noseY, hubZ: sumZ / count } : undefined;
}

/**
 * The largest distance from the propeller axis of any vertex in the cowl
 * sample: inside the centreline strip, within `NOSE_DEPTH_FRACTION` of the
 * length behind the nose ring, and within `THRUST_LINE_GUARD_FRACTION` of the
 * height of the thrust line.
 */
function noseRadiusOf(
  positions: readonly number[],
  stripHalfM: number,
  depthM: number,
  guardM: number,
  noseY: number,
  hubZ: number,
): number {
  let radius = 0;
  for (let i = 0; i + 2 < positions.length; i += 3) {
    const x = positions[i]!;
    const dz = positions[i + 2]! - hubZ;
    if (Math.abs(x) > stripHalfM) continue;
    if (positions[i + 1]! < noseY - depthM) continue;
    if (Math.abs(dz) > guardM) continue;
    const distance = Math.hypot(x, dz);
    if (distance > radius) radius = distance;
  }
  return radius;
}

export function clampPropellerRadiusM(radiusM: number): number {
  return Math.min(PROP_RADIUS_MAX_M, Math.max(PROP_RADIUS_MIN_M, radiusM));
}

/**
 * The propeller hub and disc radius for one aircraft's best renderable LOD, or
 * `undefined` when the nose cannot be classified (see the module doc). Offsets
 * are relative to the model's bounding-box centre, which is the origin
 * `aircraft-mesh.ts` recentres onto and `flight-scene.ts` poses in world space.
 */
export function propellerHubFromLod(lod: PropellerSourceLod): PropellerHub | undefined {
  const positions = lod.positions;
  if (positions.length < 9) return undefined;
  const { min, max } = extentsOf(positions);
  const spanM = max[0]! - min[0]!;
  const lengthM = max[1]! - min[1]!;
  const heightM = max[2]! - min[2]!;
  if (!(spanM > 0) || !(lengthM > 0) || !(heightM > 0)) return undefined;

  const stripHalfM = spanM * FUSELAGE_STRIP_FRACTION;
  const ring = noseRingOf(positions, stripHalfM, lengthM * NOSE_RING_DEPTH_FRACTION);
  if (!ring) return undefined;

  const noseRadiusM = noseRadiusOf(
    positions,
    stripHalfM,
    lengthM * NOSE_DEPTH_FRACTION,
    heightM * THRUST_LINE_GUARD_FRACTION,
    ring.noseY,
    ring.hubZ,
  );
  if (!(noseRadiusM > 0)) return undefined;

  return {
    forwardM: ring.noseY - (min[1]! + max[1]!) / 2,
    upM: ring.hubZ - (min[2]! + max[2]!) / 2,
    radiusM: clampPropellerRadiusM(noseRadiusM * PROP_RADIUS_PER_NOSE_RADIUS),
  };
}

/**
 * The documented fleet fallback, positioned from the model's own bounding box:
 * a `PROP_RADIUS_FLEET_M` disc at the raw nose tip, on the fuselage datum.
 * Used for the airframes `propellerHubFromLod` declines to classify — in the
 * current bake, `MORANE` and `AIRCO`, both of which are modelled with a
 * single-vertex spike for a nose and so have no ring to measure.
 */
export function fleetFallbackHub(lod: PropellerSourceLod): PropellerHub {
  const { min, max } = extentsOf(lod.positions);
  const forwardM = Number.isFinite(max[1]!) ? (max[1]! - min[1]!) / 2 : 0;
  return { forwardM, upM: 0, radiusM: PROP_RADIUS_FLEET_M };
}

/** `propellerHubFromLod`, falling back to `fleetFallbackHub` rather than declining. */
export function propellerHubOrFallback(lod: PropellerSourceLod): PropellerHub {
  return propellerHubFromLod(lod) ?? fleetFallbackHub(lod);
}
